import { modals } from '@mantine/modals'
import { Stack, Text, Group, Alert } from '@mantine/core'
import { CircleButton, CircleButtonProps } from '@packages/ui'
import { IconCheck } from '@tabler/icons-react'
import { ReactNode } from 'react'

interface ConfirmModalProps {
  title: string
  children: string | ReactNode
  confirmLabel?: string
  onConfirm: () => void | Promise<void>
  confirmButtonProps?: Partial<CircleButtonProps>
  error?: string | null
  warning?: boolean
  confirmButtonTestId?: string
  loading?: boolean
}

/**
 * Opens a confirmation modal with a centered confirm button and no cancel button.
 * This is the standard pattern for destructive or important actions in the app.
 */
export const openConfirmModal = ({
  title,
  children,
  confirmLabel,
  onConfirm,
  confirmButtonProps = {},
  error,
  warning = false,
  confirmButtonTestId,
  loading = false
}: ConfirmModalProps) => {
  modals.open({
    title,
    children: (
      <Stack>
        {typeof children === 'string' ? <Text>{children}</Text> : children}
        {error && (
          <Alert color="red" icon={<></>}>
            {error}
          </Alert>
        )}
        <Group justify="center">
          <CircleButton
            loading={loading}
            testid={confirmButtonTestId}
            icon={confirmButtonProps.icon || <IconCheck />}
            onClick={async () => {
              try {
                if (onConfirm) {
                  await onConfirm()
                }
                modals.closeAll()
              } catch (err) {
                console.error('Error in confirm action:', err)
              }
            }}
            variant={warning ? 'alert' : 'default'}
            {...confirmButtonProps}
            label={confirmLabel}
          />
        </Group>
      </Stack>
    )
  })
}
